import React, { useState, useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import theme from './BuyerChangeRequests.module.scss';
import { IState } from '@/state/IState';
import { faPlusCircle, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { LoadingIndicator, NotificationBadge, Sticky, StickyContainer } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import BuyerChangeRequestCreate from './BuyerChangeRequestCreate/BuyerChangeRequestCreate';
import { CustomLink } from '@/components/navigation';
import BuyerChangeRequestDetails from './BuyerChangeRequestDetails';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { withRouter, RouteComponentProps } from 'react-router';
import { classnames } from '@/utils/html';
import { useAppDispatch, useIsMobile } from '@/utils/hooks';
import { ChangeRequestBuyerStatusIndicator } from '@/components/StatusIndicator';
import { ConfirmButton, Button } from '@/components/Buttons';
import { orderBy } from 'lodash-es';
import { BuyerChangeRequestStatus } from '@/types';
import { loadBuyerChangeRequests } from '@/state/buyer/changeRequests/creator';
import { isDeadlineInPast } from '@/utils/date';
import BuyerPageTitle from '../BuyerPageTitle/BuyerPageTitle';
import { Card } from '@ygtechnologies/rubus-ui';

const mapDispatchToProps = () => ({});
const mapStateToProps = (state: IState) => ({
    residentialUnitId: state.buyer.permissions!.residentialUnitId,
});

type Props = RouteComponentProps<any> &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps> & {
        activeChangeRequestId: string;
    };

const BuyerChangeRequests: React.FC<Props> = ({ residentialUnitId, activeChangeRequestId, history }) => {
    const isMobile = useIsMobile();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const permissions = useSelector((state: IState) => state.buyer.permissions!);
    const projectData = useSelector((state: IState) => state.buyer.projectData!);
    const commentThreads = useSelector((state: IState) => state.buyer.commentThreads);
    const changeRequests = useSelector((state: IState) => state.buyer.changeRequests);
    const dispatch = useAppDispatch();

    const canCreateChangeRequests = projectData.changeRequestDeadline
        ? !isDeadlineInPast(projectData.changeRequestDeadline)
        : true;

    const mappedChangeRequests = useMemo(
        () =>
            changeRequests
                ? changeRequests.map((cr) => {
                      const thread = commentThreads.find((ct) => ct.threadId === cr.commentThreadId);
                      if (thread) {
                          return {
                              ...cr,
                              unreadMessages: thread.unreadMessages,
                              lastMessageDate: thread.lastMessageDate ? thread.lastMessageDate : '',
                          };
                      }
                      return { ...cr, unreadMessages: 0, lastMessageDate: -1 };
                  })
                : [],
        [changeRequests, commentThreads]
    );
    const sortedChangeRequest = useMemo(
        () =>
            orderBy(mappedChangeRequests, ['lastMessageDate', 'changeRequestId'], ['desc', 'desc']).sort((a, b) => {
                if (a.status === BuyerChangeRequestStatus.OfferReceived) return -1;
                if (b.status === BuyerChangeRequestStatus.OfferReceived) return 1;
                return 0;
            }),
        [mappedChangeRequests]
    );

    const activeChangeRequest =
        changeRequests && activeChangeRequestId
            ? changeRequests.find((x) => x.changeRequestId === +activeChangeRequestId)
            : null;

    useEffect(() => {
        if (!isMobile && !activeChangeRequestId && sortedChangeRequest && sortedChangeRequest.length > 0) {
            history.push(`change-requests/${sortedChangeRequest[0].changeRequestId}`);
        }
    }, [sortedChangeRequest, activeChangeRequestId, isMobile, history]);

    const redirectToAdminSettings = () => {
        const lastProjectId = localStorage.getItem('lastProjectId');
        const returnUrl = `/projects/${lastProjectId}/residential-units/${residentialUnitId}/changes`;
        if (!lastProjectId && !residentialUnitId) return
        history.push(`/impersonate/end?returnUrl=${encodeURIComponent(returnUrl)}`);
    };

    const handleCreated = async (id) => {
        dispatch(loadBuyerChangeRequests());
        history.push(`/change-requests/${id}`);
    };

    return (
        <>
            <div className={theme.container}>
                <BuyerPageTitle>
                    <Trans>Change requests</Trans>
                </BuyerPageTitle>

                {sortedChangeRequest ? (
                    <div className={theme.content}>
                        {(!isMobile || !activeChangeRequest) && (
                            <StickyContainer>
                                <div>
                                    <Sticky>
                                        <ul className={theme.changeRequestNavigation}>
                                            {sortedChangeRequest.map((cr) => (
                                                <li key={cr.changeRequestId}>
                                                    <Card
                                                        active={+activeChangeRequestId === cr.changeRequestId}
                                                        className={classnames(theme.changeRequestLi, {
                                                            [theme.hasOffer]:
                                                                cr.status === BuyerChangeRequestStatus.OfferReceived,
                                                        })}>
                                                        <CustomLink to={`/change-requests/${cr.changeRequestId}`}>
                                                            <div className={theme.header}>{cr.subject}</div>
                                                            <div className={theme.status}>
                                                                <ChangeRequestBuyerStatusIndicator
                                                                    value={cr.status}
                                                                    size="small"
                                                                />
                                                                <NotificationBadge
                                                                    className={theme.notificationBadge}
                                                                    value={
                                                                        cr.unreadMessages +
                                                                        (cr.status === BuyerChangeRequestStatus.OfferReceived
                                                                            ? 1
                                                                            : 0)
                                                                    }
                                                                />
                                                            </div>
                                                            {isMobile && (
                                                                <FontAwesomeIcon
                                                                    className={theme.mobileIcon}
                                                                    icon={faChevronRight}
                                                                />
                                                            )}
                                                        </CustomLink>
                                                    </Card>
                                                </li>
                                            ))}
                                            {permissions.canModify && canCreateChangeRequests && (
                                                <li className={theme.newChangeRequestButton}>
                                                    <ConfirmButton
                                                        color="text"
                                                        skipDialog={projectData.changeRequestWarningText === null}
                                                        dialogBody={projectData.changeRequestWarningText}
                                                        dialogTitle={<Trans>Please note</Trans>}
                                                        confirmButtonText={<Trans>Continue</Trans>}
                                                        cancelButtonText={<Trans>Cancel</Trans>}
                                                        icon={faPlusCircle}
                                                        iconPosition="left"
                                                        onConfirm={() => setIsCreateModalOpen(true)}>
                                                        <Trans>Create a new change request</Trans>
                                                    </ConfirmButton>
                                                </li>
                                            )}
                                            {!permissions.canModify && canCreateChangeRequests && (
                                                <li className={theme.newChangeRequestButton}>
                                                    <ConfirmButton
                                                        color="text"
                                                        dialogBody={
                                                            <Trans>You are not allowed to create change requests</Trans>
                                                        }
                                                        dialogTitle={<Trans>Please note</Trans>}
                                                        confirmButtonText={<Trans>Go to Changes</Trans>}
                                                        cancelButtonText={<Trans>Cancel</Trans>}
                                                        icon={faPlusCircle}
                                                        iconPosition="left"
                                                        onConfirm={() => redirectToAdminSettings()}>
                                                        <Trans>Create a new change request</Trans>
                                                    </ConfirmButton>
                                                </li>
                                            )}
                                        </ul>
                                    </Sticky>
                                </div>
                            </StickyContainer>
                        )}

                        {activeChangeRequest && (
                            <div className={theme.data}>
                                <BuyerChangeRequestDetails
                                    loadData={() => dispatch(loadBuyerChangeRequests())}
                                    changeRequest={activeChangeRequest}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <LoadingIndicator />
                )}
            </div>

            <BuyerChangeRequestCreate
                isOpen={isCreateModalOpen}
                onSubmit={handleCreated}
                onClose={() => setIsCreateModalOpen(false)}
            />
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(BuyerChangeRequests));
